import { useCallback } from "react";
import { isPosthogEnabled } from "config";
import {
  capturePosthogEvent,
  registerPosthogProperties,
  setPosthogGroup,
} from "config/posthog-runtime";
import { ANALYTICS_EVENTS, type FirstValueType, type ScreenViewedProperties } from "analytics";
import {
  buildPosthogPageViewProperties,
  POSTHOG_PAGEVIEW_EVENT,
  RIVETAI_ANALYTICS_SURFACE,
} from "lib/analytics/posthog-pageview";
import { getAcquisitionAttributionForEvent } from "utils/readAcquisitionAttribution";
import { useOptionalUserPreferences } from "providers/user-preferences";

/**
 * Event properties go to PostHog and the GTM dataLayer, so they never carry
 * personal data. Identity lives on the PostHog person (identify), not on events.
 */
const PERSONAL_EVENT_PROPERTY_KEYS = new Set(["email", "invitee_email", "first_name", "last_name"]);

export const stripPersonalEventProperties = (
  properties: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(properties).filter(([key]) => !PERSONAL_EVENT_PROPERTY_KEYS.has(key))
  );

const FIRST_VALUE_EVENT_TYPES: Partial<Record<string, FirstValueType>> = {
  [ANALYTICS_EVENTS.SCHEDULE_UPDATED]: "schedule_edited",
  [ANALYTICS_EVENTS.BUDGET_UPDATED]: "budget_edited",
};

const captureFirstValueOnce = (
  event: string,
  properties: Record<string, unknown>
): { projectId: string; firstValueType: FirstValueType } | null => {
  const firstValueType = FIRST_VALUE_EVENT_TYPES[event];
  const reviewedFirstValueType =
    event === ANALYTICS_EVENTS.AI_RESULT_REVIEWED && typeof properties.first_value_type === "string"
      ? (properties.first_value_type as FirstValueType)
      : undefined;
  const resolvedFirstValueType = firstValueType ?? reviewedFirstValueType;
  const projectId = properties.project_id;
  if (!resolvedFirstValueType || (typeof projectId !== "string" && typeof projectId !== "number"))
    return null;

  const storageKey = `rivetai:first-value:${String(projectId)}`;
  try {
    if (typeof window !== "undefined" && window.localStorage.getItem(storageKey)) return null;
    if (typeof window !== "undefined")
      window.localStorage.setItem(storageKey, resolvedFirstValueType);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("rivetai:first-value-completed", {
          detail: { projectId: String(projectId) },
        })
      );
    }
  } catch {
    // Storage can be blocked. PostHog's earliest event remains the source of truth.
  }

  const firstValueProperties = {
    project_id: String(projectId),
    ...(properties.workspace_id ? { workspace_id: properties.workspace_id } : {}),
    first_value_type: resolvedFirstValueType,
  };
  pushToGTMDataLayer({ event: ANALYTICS_EVENTS.FIRST_VALUE_COMPLETED, ...firstValueProperties });
  capturePosthogEvent(ANALYTICS_EVENTS.FIRST_VALUE_COMPLETED, firstValueProperties);
  return { projectId: String(projectId), firstValueType: resolvedFirstValueType };
};

const pushToGTMDataLayer = (payload: Record<string, unknown>) => {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch (error) {
    console.error("Failed to push event to dataLayer:", error);
  }
};

export const useAnalytics = () => {
  const preferencesContext = useOptionalUserPreferences();
  const patchPreferences = preferencesContext?.patchPreferences;
  const trackEvent = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      const attribution =
        event === ANALYTICS_EVENTS.SIGN_UP ||
        event === ANALYTICS_EVENTS.SIGN_UP_COMPLETED ||
        event === ANALYTICS_EVENTS.SIGNUP_PAGE_VIEWED ||
        event === ANALYTICS_EVENTS.SIGNUP_FORM_STARTED ||
        event === ANALYTICS_EVENTS.SIGNUP_SUBMITTED ||
        event === ANALYTICS_EVENTS.SIGN_UP_STARTED
          ? getAcquisitionAttributionForEvent()
          : {};
      const mergedProperties = stripPersonalEventProperties({
        ...attribution,
        ...(properties ?? {}),
      });
      const payload = {
        event,
        analytics_schema_version: 2,
        rivetai_surface: RIVETAI_ANALYTICS_SURFACE.productApp,
        ...mergedProperties,
      };
      pushToGTMDataLayer(payload);
      capturePosthogEvent(event, mergedProperties);
      const firstValue = captureFirstValueOnce(event, mergedProperties);
      if (firstValue && patchPreferences) {
        patchPreferences({
          onboarding: {
            projectSetupStatus: "first_value_completed",
            projectId: firstValue.projectId,
            firstValueType: firstValue.firstValueType,
            firstValueCompletedAt: new Date().toISOString(),
          },
        });
      }
    },
    [patchPreferences]
  );

  const trackPageView = useCallback(
    (screenName: string, properties?: Partial<ScreenViewedProperties>) => {
      const screenPath = typeof window !== "undefined" ? window.location.pathname : "";
      const search = typeof window !== "undefined" ? window.location.search.replace(/^\?/, "") : "";
      const payload = {
        event: ANALYTICS_EVENTS.SCREEN_VIEWED,
        screen_name: screenName,
        screen_path: screenPath,
        ...properties,
      };
      pushToGTMDataLayer(payload);
      capturePosthogEvent(POSTHOG_PAGEVIEW_EVENT, {
        ...buildPosthogPageViewProperties(RIVETAI_ANALYTICS_SURFACE.productApp, screenPath, search),
        screen_name: screenName,
        screen_path: screenPath,
        ...properties,
      });
    },
    []
  );

  const setWorkspaceGroup = useCallback(
    (workspaceId: string, properties?: { name?: string; plan?: string; member_count?: number }) => {
      if (!isPosthogEnabled) return;
      setPosthogGroup("workspace", workspaceId, properties);
    },
    []
  );

  const setProjectContext = useCallback(
    (projectId: string, workspaceId: string, projectName?: string) => {
      if (!isPosthogEnabled) return;
      registerPosthogProperties({
        active_project_id: projectId,
        active_workspace_id: workspaceId,
        ...(projectName && { active_project_name: projectName }),
      });
    },
    []
  );

  return {
    trackEvent,
    trackPageView,
    setWorkspaceGroup,
    setProjectContext,
    ANALYTICS_EVENTS,
  };
};
